import React, { useContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import ReactHtmlParser from 'react-html-parser';
import { CSSTransition } from 'react-transition-group';
import { useTranslation } from 'react-i18next';
import { ThemeContext } from '../../App/ThemeContext';
import { ReactComponent as ArrowTop } from '../../../assets/imgs/svg/arrow-top.svg';
import { ReactComponent as ArrowBottom } from '../../../assets/imgs/svg/arrow-bottom.svg';
import classes from './NavHeader.module.scss';

const findIndex = (moduleId, modules) => modules.findIndex((elem) => elem.id.toString() === moduleId.toString());

const NavBtns = (props, compact, t) => {
  const {
    isVisible,
    onVisibleChange,
    onPrevClick,
    onNextClick,
    curContent,
    modules,
  } = props;

  const filteredModules = [];

  for (let i = 0; i < modules.length; i += 1) {
    if (
      i === 0
      || (modules[i].show
      && (modules[i].opened === 'true'
      || modules[i - 1].opened === 'true'))
    ) filteredModules.push(modules[i]);
  }

  const PrevIsDisabled = findIndex(curContent, filteredModules) === 0;
  const NextIsDisabled = findIndex(curContent, filteredModules) === filteredModules.length - 1;

  return (
    <>
      <div
        role="button"
        aria-label="Hide"
        className={`${classes.Menu} ${isVisible && !compact ? '' : classes.closed}`}
        onClick={() => onVisibleChange(compact)}
        onKeyDown={(event) => { if (event.key === 'Enter') onVisibleChange(compact); }}
        tabIndex="0"
        alt={compact ? t('nav.showNavbar') : t('nav.hideNavbar')}
      />
      <div
        role="button"
        className={`${classes.UpIcon} ${PrevIsDisabled ? classes.disabled : ''}`}
        title={t('nav.prev')}
        onClick={onPrevClick}
        onKeyDown={(event) => { if (!PrevIsDisabled && event.key === 'Enter') onPrevClick(); }}
        alt={t('nav.prev')}
        tabIndex="0"
      >
        <ArrowTop style={{ width: 14, height: 14 }} />
        { !compact && <p>{t('nav.prev')}</p> }
      </div>
      <div
        role="button"
        className={`${classes.DownIcon} ${NextIsDisabled ? classes.disabled : ''}`}
        title={t('nav.next')}
        onClick={onNextClick}
        onKeyDown={(event) => { if (!NextIsDisabled && event.key === 'Enter') onNextClick(); }}
        alt={t('nav.next')}
        tabIndex="0"
      >
        { !compact && <p>{t('nav.next')}</p> }
        <ArrowBottom style={{ width: 14, height: 14 }} />
      </div>
    </>
  );
};

const minFontSize = 0.08;
const maxFontSize = 0.17;

const NavHeader = (props) => {
  const { t } = useTranslation();
  const { isVisible, commonSettings } = props;
  const { theme } = useContext(ThemeContext);
  const [ fontSize, setFontSize ] = useState(0.16);

  const titleRef = useRef(null);
  const titleContainerRef = useRef(null);

  useEffect(() => {
    if (titleContainerRef.current != null && titleRef.current != null) {
      if (titleRef.current.offsetWidth > titleContainerRef.current.offsetWidth ||
          titleRef.current.offsetHeight > titleContainerRef.current.offsetHeight
        )
      {
        if (fontSize > minFontSize && fontSize < maxFontSize)
          setFontSize(prevValue => prevValue -0.02);
      }
    }
  }, [titleRef.current, titleContainerRef.current, fontSize])

  let NavHiddenComponent = (
    <CSSTransition
      mountOnEnter
      unmountOnExit
      in={!isVisible}
      timeout={1500}
      classNames={{
        enterActive: classes.show,
        enterDone: classes.show,
      }}
    >
      <div className={`${classes.NavHidden} ${theme ? classes[theme] : ''}`}>
        {NavBtns(props, true, t)}
      </div>
    </CSSTransition>
  );

  if (theme) {
    NavHiddenComponent = (
      <div className={`${classes.NavHidden} ${isVisible ? '' : classes.show } ${theme ? classes[theme] : ''}`}>
        {NavBtns(props, true, t)}
      </div>
    );
  }

  return (
    <>
      {NavHiddenComponent}
      <div style={{ backgroundImage: `url(${commonSettings.background})` }} className={`${classes.NavHeader} ${theme ? classes[theme] : ''}`}>
        <div className={classes.Logotype}>
          <img className={classes.Img} alt="" src={commonSettings.logo} />
          <p
            ref={titleContainerRef}
            className={classes.Title}>
            <span
              ref={titleRef} 
              style={{ fontSize: `${fontSize}rem` }}>
                {ReactHtmlParser(commonSettings.title)}
            </span>
          </p>
        </div>
        {NavBtns(props, false, t)}
      </div>
    </>
  );
};

export default React.memo(NavHeader);

NavHeader.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  commonSettings: PropTypes.shape({
    base: PropTypes.string.isRequired,
    lang: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    logo: PropTypes.string.isRequired,
    background: PropTypes.string.isRequired,
    theme: PropTypes.string.isRequired,
  }).isRequired,
};

NavBtns.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  onVisibleChange: PropTypes.func.isRequired,
  onPrevClick: PropTypes.func.isRequired,
  onNextClick: PropTypes.func.isRequired,
  curContent: PropTypes.number.isRequired,
  modules: PropTypes.arrayOf(PropTypes.object).isRequired,
};
